import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './EditMaxPractitionersLimit.css';
import FormatUrl from '../../utils/UrlFormatter';
import { getClinicId } from '../../utils/config';
import { toast } from 'react-toastify';

const EditMaxPractitionersLimit = ({ user }) => {
  const { t } = useTranslation();
  const [initialLimit, setInitialLimit] = useState(null); // Constant initial value
  const [newLimit, setNewLimit] = useState(null);
  const [minAllowedLimit, setMinAllowedLimit] = useState(1);

  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingPractitioners, setLoadingPractitioners] = useState(true);

  const [isModified, setIsModified] = useState(false);
  const [showRestrictionMsg, setShowRestrictionMsg] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);

  const clinicId = getClinicId(user);

  const fetchClinicConfig = useCallback(async () => {
    if (!clinicId) return;

    try {
      const csrfRes = await fetch(FormatUrl('/csrf-token/'), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const { csrfToken } = await csrfRes.json();

      const configUrl = FormatUrl(`/get-clinic-configuration/?clinic_id=${clinicId}`);
      const response = await fetch(configUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (data.status === 'Success') {
        const currentLimit = Number(data.max_practitioners || 0);
        setInitialLimit(currentLimit);
        setNewLimit(currentLimit);
        setIsModified(false);
        setShowRestrictionMsg(false);
      } else {
        console.error('Unexpected status from config API', data);
      }
    } catch (error) {
      console.error('Error fetching max practitioners:', error);
    } finally {
      setLoadingConfig(false);
    }
  }, [clinicId]);

  const fetchPractitionerCount = useCallback(async () => {
    if (!clinicId) return;

    try {
      const res = await fetch(FormatUrl(`/get-doctors/?clinic_id=${clinicId}`), {
        method: 'GET',
        credentials: 'include',
      });

      const data = await res.json();
      if (data?.status === 'success' && Array.isArray(data.practitioners)) {
        setMinAllowedLimit(data.practitioners.length);
      } else {
        console.error('Unexpected practitioners response', data);
      }
    } catch (error) {
      console.error('Error fetching practitioner list:', error);
    } finally {
      setLoadingPractitioners(false);
    }
  }, [clinicId]);

  useEffect(() => {
    fetchClinicConfig();
    fetchPractitionerCount();
  }, [fetchClinicConfig, fetchPractitionerCount]);

  const handleChange = (delta) => {
    const updated = newLimit + delta;

    if (updated < minAllowedLimit) {
      setShowRestrictionMsg(true);
      return;
    }

    setShowRestrictionMsg(false);
    setNewLimit(updated);
    setIsModified(updated !== initialLimit);
  };

  const handleSave = async () => {
    if (!clinicId) return;

    setSubmitting(true);
    try {
      const csrfRes = await fetch(FormatUrl('/csrf-token/'), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const { csrfToken } = await csrfRes.json();

      const formData = new FormData();
      formData.append('clinic_id', clinicId);
      formData.append('max_practitioners', newLimit);

      const response = await fetch(
        FormatUrl('/edit-clinic-configuration-max-practitioners'),
        {
          method: 'POST',
          headers: {
            'X-CSRFToken': csrfToken,
          },
          credentials: 'include',
          body: formData,
        }
      );

      const result = await response.json();

      if (response.ok && result.status === 'Success') {
        toast.success(t('clinic_settings.success_update'));
        setSuccessMessageVisible(true);
        setTimeout(() => setSuccessMessageVisible(false), 5000);
        // ❌ Do not reset initialLimit
        // ✅ Only refresh active practitioners
        fetchPractitionerCount();
      } else {
        toast.error(result.error || 'Failed to update configuration.');
      }
    } catch (error) {
      console.error('Update failed:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isLoading = loadingConfig || loadingPractitioners;

  return (
    <div className="prac-limit-container">
      <h2 className="prac-limit-heading">{t('clinic_settings.edit_prac_limit_heading')}</h2>
      <p className="prac-limit-description">
        {t('clinic_settings.edit_prac_limit_description')}
      </p>

      {isLoading ? (
        <p className="prac-limit-loading">{t('clinic_settings.loading')}</p>
      ) : (
        <>
          <div className="prac-limit-initial-box">
            <span className="prac-limit-label" style={{ marginRight: '0.5rem' }}>
            {t('clinic_settings.subscription_chosen_signup')}
            </span>
            <span className="prac-limit-badge">{initialLimit ?? '—'}</span>
          </div>

          <div className="prac-limit-initial-box" style={{ marginTop: '0.5rem' }}>
            <span className="prac-limit-label" style={{ marginRight: '0.5rem' }}>
            {t('clinic_settings.active_subscriptions')}
            </span>
            <span className="prac-limit-badge">{minAllowedLimit ?? '—'}</span>
          </div>

          <div className="prac-limit-initial-box" style={{ marginTop: '0.5rem' }}>
            <span className="prac-limit-label" style={{ marginRight: '0.5rem' }}>
            {t('clinic_settings.updated_max_subscriptions')}
            </span>
            <span className="prac-limit-badge">{newLimit ?? '—'}</span>
          </div>

          <p className="prac-limit-description" style={{ marginTop: '1rem' }}>
          {t('clinic_settings.adjust_subscription_count')}
          </p>

          <div className="prac-limit-counter">
            <button
              onClick={() => handleChange(-1)}
              className="counter-btn"
              disabled={newLimit <= minAllowedLimit}
              aria-label="Decrease limit"
            >
              −
            </button>

            <span className="counter-value" aria-live="polite">
              {newLimit ?? '—'}
            </span>

            <button
              onClick={() => handleChange(1)}
              className="counter-btn"
              aria-label="Increase limit"
            >
              +
            </button>
          </div>

          <p className="unlock-hint" style={{ marginTop: '1rem', fontSize: '0.95rem', }}>
          {t('clinic_settings.subscription_count_hint')}
          </p>

          {showRestrictionMsg && (
            <p className="prac-limit-warning">
              {t('clinic_settings.warning_below_limit')}
            </p>
          )}

          {successMessageVisible && (
            <p className="prac-limit-success">
              {t('clinic_settings.success_update')}
            </p>
          )}

          <button
            className="prac-limit-save-btn"
            disabled={!isModified || submitting}
            onClick={handleSave}
            aria-disabled={!isModified || submitting}
          >
            {submitting ? t('clinic_settings.button_saving') : t('clinic_settings.button_save')}
          </button>
        </>
      )}
    </div>
  );
};

export default EditMaxPractitionersLimit;
